import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tJTIwd2FybSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MjY5MjMyOXww&ixlib=rb-4.1.0&q=85";
const SHOWROOM = "https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BRANDS = ["Leuchten Direkt", "Trio Lighting", "Nordlux", "Eglo", "Paul Neuhaus", "Fischer & Honsel", "Globo", "Brilliant"];
const TRUST = [
  ["100 % authentique", "Pièces d'origine des grandes marques, vérifiées une à une."],
  ["Garantie 1 an", "Chaque luminaire est couvert, comme en boutique."],
  ["Livraison soignée", "Emballage protégé, offerte dès 50 € d'achat."],
  ["Stock limité", "Fins de série et surstocks : quantités réelles, sans réassort."],
];

export default function Home() {
  useSeo("home");
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/products", { params: { limit: 100 } })])
      .then(([c, p]) => {
        setCategories(c.data);
        setFeatured(p.data.filter((x) => x.featured).slice(0, 8));
        const cnt = {};
        p.data.forEach((x) => { cnt[x.category_slug] = (cnt[x.category_slug] || 0) + 1; });
        setCounts(cnt);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-8 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-5 animate-fade-up">
            Collection Déstockage · Édition limitée
          </div>
          <h1 className="font-serif font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.03] tracking-tight text-ink animate-fade-up" style={{ animationDelay: "80ms" }}>
            La belle lumière,<br />
            <em className="text-copper not-italic font-medium italic">enfin accessible.</em>
          </h1>
          <p className="text-base md:text-[16.5px] leading-relaxed text-graphite max-w-md mt-6 font-light animate-fade-up" style={{ animationDelay: "160ms" }}>
            Des grandes marques du luminaire, sélectionnées et déstockées jusqu'à −70 %. Pièces authentiques, stock limité, élégance intacte.
          </p>
          <div className="flex flex-wrap items-center gap-5 mt-9 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/boutique" data-testid="hero-cta-shop" className="bg-ink text-cream px-8 py-4 text-[13px] uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
              Découvrir le déstockage
            </Link>
            <Link to="/boutique" className="text-[13.5px] text-ink border-b border-ink/30 hover:border-ink pb-0.5 transition-colors">
              Voir les nouveautés →
            </Link>
          </div>
          <div className="flex gap-10 mt-12 pt-8 border-t border-ink/10 animate-fade-up" style={{ animationDelay: "320ms" }}>
            {[["−70%", "jusqu'à"], [`+${Object.values(counts).reduce((a, b) => a + b, 0) || 16}`, "produits"], ["100%", "authentique"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-3xl font-semibold text-ink">{n}</div>
                <div className="text-[11.5px] uppercase tracking-wide text-ash mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative aspect-[4/5] overflow-hidden bg-sand">
          <img src={HERO} alt="Suspension en ambiance" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute top-5 right-5 bg-copper text-white text-xs tracking-wide px-3.5 py-2 font-medium">−60%</div>
        </div>
      </section>

      {/* Brand marquee */}
      <section className="border-y border-ink/10 overflow-hidden py-6">
        <div className="flex w-max lm-marquee">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={i} className="font-serif text-2xl tracking-wide text-ash whitespace-nowrap px-10">{b}</span>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-2">
        <div className="flex items-end justify-between mb-7">
          <h2 className="font-serif font-medium text-3xl md:text-4xl text-ink">Par catégorie</h2>
          <Link to="/boutique" data-testid="see-all-link" className="text-[13px] text-graphite border-b border-graphite/30 hover:border-graphite pb-0.5 transition-colors">Tout voir →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/categorie/${c.slug}`}
              data-testid={`home-category-${c.slug}`}
              className="group relative aspect-[1/1.15] overflow-hidden bg-sand animate-fade-up"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <h3 className="font-serif text-2xl text-cream">{c.name}</h3>
                <p className="text-cream/70 text-[11.5px] tracking-wide mt-0.5">{counts[c.slug] || 0} pièces</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured — Les pièces à saisir */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-4">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-2.5">Stock limité</p>
            <h2 className="font-serif font-medium text-3xl md:text-[38px] text-ink">Les pièces à saisir</h2>
          </div>
          <Link to="/boutique" className="text-[13px] text-graphite border-b border-graphite/30 hover:border-graphite pb-0.5 transition-colors">Tout le déstockage →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10 md:gap-x-6 md:gap-y-12">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)
            : featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Editorial dark — Notre promesse */}
      <section className="bg-ink text-cream mt-20 md:mt-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 items-stretch">
          <div className="py-16 md:py-24 lg:pr-16 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-5">Notre promesse</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">Le luxe du luminaire,<br />sans le prix du neuf.</h2>
            <p className="text-[16px] leading-relaxed text-cream/70 font-light max-w-md mb-9">
              Fins de série, retours boutique et surstocks des plus belles marques. Chaque pièce est contrôlée, garantie, et proposée à prix déstockage. Quand c'est parti, c'est parti.
            </p>
            <Link to="/boutique" className="self-start bg-gold text-ink px-8 py-4 text-[13px] uppercase tracking-[0.15em] hover:bg-cream transition-colors">
              Explorer la boutique
            </Link>
          </div>
          <div className="relative min-h-[340px] lg:min-h-[480px] overflow-hidden">
            <img src={SHOWROOM} alt="Showroom" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-9">
          {TRUST.map(([t, d]) => (
            <div key={t} className="border-t border-ink/15 pt-5">
              <div className="font-serif text-2xl font-semibold text-ink mb-2">{t}</div>
              <p className="text-sm leading-relaxed text-graphite font-light">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-sand">
        <div className="max-w-2xl mx-auto px-6 py-16 md:py-20 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-copper font-medium mb-4">Avant tout le monde</p>
          <h2 className="font-serif font-medium text-3xl md:text-4xl text-ink leading-tight mb-4">Recevez les arrivages déstockage</h2>
          <p className="text-base text-graphite font-light mb-8">Les pièces partent vite. Soyez prévenu·e en avant-première.</p>
          <form
            onSubmit={(e) => { e.preventDefault(); toast.success("Merci ! Vous êtes inscrit·e aux alertes déstockage."); e.target.reset(); }}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input type="email" required placeholder="Votre adresse e-mail" data-testid="newsletter-email" className="flex-1 border border-ink/20 bg-cream px-5 py-3.5 text-sm text-ink outline-none focus:border-ink" />
            <button type="submit" data-testid="newsletter-submit" className="bg-ink text-cream px-7 py-3.5 text-[13px] uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors">S'inscrire</button>
          </form>
        </div>
      </section>
    </div>
  );
}
